import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { NumericFormat } from "react-number-format";

import {
  currency,
  currentTenant,
  thousandsdisplay,
  userid,
} from "../../Common/Credentials.js";
import { spin } from "../../Common/NewLoader.jsx";
import { appmasterid } from "../../Common/Permissions.js";
import SalesCalculationServices from "./SalesCalculationServices.js";

import EditIcon from "../../images/Icons/edit.png";
import DeleteIcon from "../../images/Icons/delete.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

const SalesCalTable = () => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshItems = useCallback(() => {
    setLoading(true);
    SalesCalculationServices.SalesCalTable(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => a.name?.localeCompare(b.name));
        setTableData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshItems();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshItems]);

  useEffect(() => {
    updateFilteredData();
  }, [tableData, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        case 'cost':
          aValue = parseFloat(a.cost) || 0;
          bValue = parseFloat(b.cost) || 0;
          break;
        case 'margin':
          aValue = parseFloat(a.margin) || 0;
          bValue = parseFloat(b.margin) || 0;
          break;
        case 'discount':
          aValue = parseFloat(a.discount) || 0;
          bValue = parseFloat(b.discount) || 0;
          break;
        case 'bankintrest':
          aValue = parseFloat(a.bankintrest) || 0;
          bValue = parseFloat(b.bankintrest) || 0;
          break;
        case 'transportCharges':
          aValue = parseFloat(a.transportCharges) || 0;
          bValue = parseFloat(b.transportCharges) || 0;
          break;
        case 'grossValue':
          aValue = parseFloat(a.grossValue) || 0;
          bValue = parseFloat(b.grossValue) || 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = tableData;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = tableData.filter(item =>
        item.name?.toLowerCase().includes(lowerSearchTerm) ||
        item.type?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deletesalescal = (id, itemName) => {
    Swal.fire({
      title: `Delete Sales Calculation for ${itemName}?`,
      text: "Are you sure you want to delete this calculation?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        SalesCalculationServices.DeleteSalesCal(id)
          .then(() => {
            Swal.fire({
              title: "Sales Calculation deleted successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshItems();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} calculations
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Format currency with NumericFormat
  const formatCurrency = (value) => (
    <NumericFormat
      displayType={"text"}
      value={value}
      decimalScale={2}
      fixedDecimalScale={true}
      thousandSeparator={true}
      thousandsGroupStyle={thousandsdisplay}
      className="UI-currency-value"
    />
  );

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No sales calculations found matching your search' : 'No sales calculations available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <div className="UI-table-scroll-wrapper">
            <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
              <thead className="UI-table-header">
                <tr>
                  <th
                    className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Item Name
                      <SortIndicator columnKey="name" />
                    </div>
                  </th>
                  <th
                    className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'type' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('type')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Type
                      <SortIndicator columnKey="type" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'cost' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('cost')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Price ({currency})
                      <SortIndicator columnKey="cost" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'margin' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('margin')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Margin ({currency})
                      <SortIndicator columnKey="margin" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'discount' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('discount')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Discount ({currency})
                      <SortIndicator columnKey="discount" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'bankintrest' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('bankintrest')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Bank Interest (%)
                      <SortIndicator columnKey="bankintrest" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'transportCharges' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('transportCharges')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Transport Charge
                      <SortIndicator columnKey="transportCharges" />
                    </div>
                  </th>
                  <th
                    className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'grossValue' ? 'UI-th-sorted' : ''}`}
                    onClick={() => sortData('grossValue')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="UI-th-content">
                      Sales Price ({currency})
                      <SortIndicator columnKey="grossValue" />
                    </div>
                  </th>
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentPageData.map((item) => (
                  <tr
                    key={item.id}
                    className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                    onMouseEnter={() => setHoveredRow(item.id)}
                    onMouseLeave={() => {
                      setHoveredRow(null);
                      setHoveredAction(null);
                    }}
                  >
                    {/* Item Name Cell */}
                    <td
                      className={`UI-td UI-item-name ${hoveredRow === item.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    >
                      {highlightSearchText(item.name, searchTerm)}
                    </td>

                    {/* Type Cell */}
                    <td
                      className={`UI-td UI-type ${hoveredRow === item.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    >
                      {highlightSearchText(item.type, searchTerm)}
                    </td>

                    {/* Price Cell */}
                    <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {formatCurrency(item.cost)}
                    </td>

                    {/* Margin Cell */}
                    <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {formatCurrency(item.margin)}
                    </td>

                    {/* Discount Cell */}
                    <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {formatCurrency(item.discount)}
                    </td>

                    {/* Bank Interest Cell */}
                    <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <span className="UI-percentage-badge">
                        {item.bankintrest}%
                      </span>
                    </td>

                    {/* Transport Charge Cell */}
                    <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {formatCurrency(item.transportCharges)}
                    </td>

                    {/* Sales Price Cell */}
                    <td className={`UI-td UI-numeric UI-sales-price ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <span className="UI-currency-highlight">
                        {formatCurrency(item.grossValue)}
                      </span>
                    </td>

                    {/* Actions Cell */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                        <div className="UI-action-buttons">
                          {/* Edit Calculation */}
                          <Tooltip title="Edit Sales Calculation" arrow slotProps={{
                            popper: {
                              sx: { pointerEvents: 'none' },
                            },
                          }}>
                            <Link
                              to="/salescal"
                              state={{
                                id: item.id,
                                pagename: "Edit Sales Calculation"
                              }}
                              className={`UI-action-button UI-edit-button ${hoveredAction === `edit-${item.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`edit-${item.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <img
                                src={EditIcon}
                                alt="Edit"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                              />
                            </Link>
                          </Tooltip>

                          {/* Delete Calculation */}
                          <Tooltip title="Delete Sales Calculation" arrow slotProps={{
                            popper: {
                              sx: { pointerEvents: 'none' },
                            },
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deletesalescal(item.id, item.name);
                              }}
                              className={`UI-action-button UI-delete-button ${hoveredAction === `delete-${item.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`delete-${item.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <img
                                src={DeleteIcon}
                                alt="Delete"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
      <div className="UI-card">
        {/* Header */}
        <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
        <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Sales Calculation",path:null}]}/>

          <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
            <div>
              <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                Sales Calculation
              </h1>
              <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                Manage sales calculations and pricing
              </p>
            </div>

            <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
              {/* Mobile Search */}
              {isMobile && (
                <input
                  type="text"
                  placeholder="Search calculations..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="UI-mobile-search-input"
                />
              )}

              <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                {/* Desktop Search Toggle */}
                {!isMobile && (
                  <div
                    className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    onMouseEnter={() => setHovered("search")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i
                      className="fa fa-search UI-search-icon"
                    />
                    {!showSearch && "Search"}
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search calculations..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-search-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          if (searchTerm.trim() === "") {
                            setShowSearch(false);
                          }
                        }}
                      />
                    )}
                  </div>
                )}

                {/* New Calculation button */}
                {(appmasterid === "2" || appmasterid === "3") && (
                  <Link
                    to="/salescal"
                    state={{ id: -1, pagename: "New Sales Calculation" }}
                    className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                    onMouseEnter={() => setHovered("new")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-plus" /> {isMobile ? "New" : "New Calculation"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="UI-loading-container">
            {spin}
          </div>
        ) : (
          renderTable()
        )}
      </div>
    </div>
  );
};

export default SalesCalTable;